/**
 * types.ts;
 * Définition de tous les types et interfaces TypeScript pour l'application;
 */
import Konva from 'konva';
/**
 * Type des objets que l'on peut ajouter sur le canvas;
 */
export type ObjectType = 'text' | 'image' | 'photoSpace' | 'background';
/**
 * Interface pour les propriétés communes à tous les objets du template;
 */
export interface TemplateObject {
  id: string;
  type: ObjectType;
  node: Konva.Node;
}
/**
 * Interface pour l'état complet du canvas;
 */
export interface CanvasState {
  json: string;
  timestamp: number;
}
/**
 * Interface pour les dimensions du template;
 */
export interface TemplateDimensions {
  width: number;
  height: number;
}
/**
 * Interface pour les propriétés du texte;
 */
export interface TextProperties {
  content: string;
  fontFamily: string;
  fontSize: number;
  fill: string;
  fontStyle: string; // 'normal', 'bold', 'italic', 'bold italic'
  align: string; // 'left', 'center', 'right'
}
/**
 * Interface pour les propriétés communes d'un objet;
 */
export interface CommonProperties {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  scaleX: number;
  scaleY: number;
}
/**
 * Options pour l'export PNG;
 */
export interface ExportOptions {
  pixelRatio: number;
  mimeType: string;
  quality: number;
}
/**
 * Interface pour la configuration de l'application;
 */
export interface AppConfig {
  defaultWidth: number;
  defaultHeight: number;
  defaultBackgroundColor: string;
  maxHistoryStates: number;
  transformerAnchorSize: number;
}
/**
 * Configuration par défaut;
 */
export const DEFAULT_CONFIG: AppConfig = {
  defaultWidth: 800,
  defaultHeight: 600,
  defaultBackgroundColor: '#ffffff',
  maxHistoryStates: 50,
  transformerAnchorSize: 10,
};
// Types pour les formats de template;
export type TemplateUnit = 'px' | 'cm' | 'mm' | 'inch';
export type TemplateOrientation = 'landscape' | 'portrait' | 'square';
export interface TemplateFormat {
  name: string;
  width: number;
  height: number;
  unit: TemplateUnit;
  orientation: TemplateOrientation;
  category: 'digital' | 'print' | 'social' | 'custom';
  description?: string;
}
export interface TemplatePreset {
  name: string;
  formats: TemplateFormat[];
}
// Formats prédéfinis;
export const TEMPLATE_PRESETS: TemplatePreset[] = [
  {
    name: 'Digital',
    formats: [
      {
        name: 'HD 1080p',
        width: 1920,
        height: 1080,
        unit: 'px',
        orientation: 'landscape',
        category: 'digital',
        description: 'Full HD standard',
      },
      {
        name: 'HD Vertical',
        width: 1080,
        height: 1920,
        unit: 'px',
        orientation: 'portrait',
        category: 'digital',
        description: 'Format vertical HD',
      },
      {
        name: 'Carré HD',
        width: 1080,
        height: 1080,
        unit: 'px',
        orientation: 'square',
        category: 'digital',
        description: 'Format carré HD',
      },
      {
        name: '4K',
        width: 3840,
        height: 2160,
        unit: 'px',
        orientation: 'landscape',
        category: 'digital',
        description: 'Ultra HD 4K',
      },
    ],
  },
  {
    name: 'Impression',
    formats: [
      {
        name: 'A4',
        width: 21,
        height: 29.7,
        unit: 'cm',
        orientation: 'portrait',
        category: 'print',
        description: 'Format A4 standard',
      },
      {
        name: 'A4 Paysage',
        width: 29.7,
        height: 21,
        unit: 'cm',
        orientation: 'landscape',
        category: 'print',
        description: 'Format A4 paysage',
      },
      {
        name: 'A3',
        width: 29.7,
        height: 42,
        unit: 'cm',
        orientation: 'portrait',
        category: 'print',
        description: 'Format A3',
      },
      {
        name: '10x15 cm',
        width: 10,
        height: 15,
        unit: 'cm',
        orientation: 'portrait',
        category: 'print',
        description: 'Format photo standard',
      },
      {
        name: '15x20 cm',
        width: 15,
        height: 20,
        unit: 'cm',
        orientation: 'portrait',
        category: 'print',
        description: 'Format photo portrait',
      },
      {
        name: '20x30 cm',
        width: 20,
        height: 30,
        unit: 'cm',
        orientation: 'portrait',
        category: 'print',
        description: 'Format affiche moyenne',
      },
    ],
  },
  {
    name: 'Réseaux Sociaux',
    formats: [
      {
        name: 'Instagram Post',
        width: 1080,
        height: 1080,
        unit: 'px',
        orientation: 'square',
        category: 'social',
        description: 'Publication Instagram carrée',
      },
      {
        name: 'Instagram Story',
        width: 1080,
        height: 1920,
        unit: 'px',
        orientation: 'portrait',
        category: 'social',
        description: 'Story Instagram verticale',
      },
      {
        name: 'Facebook Cover',
        width: 820,
        height: 312,
        unit: 'px',
        orientation: 'landscape',
        category: 'social',
        description: 'Couverture Facebook',
      },
      {
        name: 'Twitter Header',
        width: 1500,
        height: 500,
        unit: 'px',
        orientation: 'landscape',
        category: 'social',
        description: 'En-tête Twitter',
      },
    ],
  },
];
// Utilitaires de conversion d'unités;
export class UnitConverter {
  static pxToCm(px: number, dpi: number = 96): number {
    return (px * 2.54) / dpi;
  }
  static cmToPx(cm: number, dpi: number = 96): number {
    return (cm * dpi) / 2.54;
  }
  static pxToMm(px: number, dpi: number = 96): number {
    return this.pxToCm(px, dpi) * 10;
  }
  static mmToPx(mm: number, dpi: number = 96): number {
    return this.cmToPx(mm / 10, dpi);
  }
  static pxToInch(px: number, dpi: number = 96): number {
    return px / dpi;
  }
  static inchToPx(inch: number, dpi: number = 96): number {
    return inch * dpi;
  }
  static convert(
    value: number,
    fromUnit: TemplateUnit,
    toUnit: TemplateUnit,
    dpi: number = 96
  ): number {
    // Convertir d'abord en pixels;
    let pxValue: number;
    switch (fromUnit) {
      case 'px':
        pxValue = value;
        break;
      case 'cm':
        pxValue = this.cmToPx(value, dpi);
        break;
      case 'mm':
        pxValue = this.mmToPx(value, dpi);
        break;
      case 'inch':
        pxValue = this.inchToPx(value, dpi);
        break;
    }
    // Convertir en unité cible;
    switch (toUnit) {
      case 'px':
        return pxValue;
      case 'cm':
        return this.pxToCm(pxValue, dpi);
      case 'mm':
        return this.pxToMm(pxValue, dpi);
      case 'inch':
        return this.pxToInch(pxValue, dpi);
    }
  }
}
// ============================================================================
// TYPES POUR LE NOUVEAU SYSTÈME D'INTERFACE (Phase 1-3);
// ============================================================================
/**
 * Système de panneaux à onglets pour la sidebar;
 */
export interface SidebarTab {
  id: string;
  icon: string; // Nom d'icône Lucide;
  label: string;
  content: () => HTMLElement;
  badge?: number; // Nombre d'éléments (optionnel,);
}
/**
 * Types pour l'outil texte avancé
 */
export interface TextStyle {
  id: string;
  name: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  color: string;
  lineHeight?: number;
  letterSpacing?: number;
}
export type TextAutoResize = 'none' | 'width' | 'height' | 'both';
export interface TextNodeExtended extends BaseNode {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  color: string;
  alignment: 'left' | 'center' | 'right' | 'justify';
  lineHeight: number;
  letterSpacing: number;
  wrap: boolean;
  autoResize: TextAutoResize;
  // Effets;
  shadow?: {
    offsetX: number;
    offsetY: number;
    blur: number;
    color: string;
  };
  stroke?: {
    width: number;
    color: string;
  };
  background?: {
    color: string;
    cornerRadius: number;
  };
  // Style nommé
  styleId?: string; // Référence à un TextStyle;
  // Variables;
  variables?: Record<string, string>; // Ex: {{nom}} -> "John"
}
/**
 * Filtres pour les images;
 */
export interface PhotoFilters {
  grayscale?: boolean;
  sepia?: boolean;
  brightness?: number; // -100 to +100;
  contrast?: number; // -100 to +100;
  saturation?: number; // -100 to +100;
}
/**
 * Extension du type NodeModel pour inclure les nouveaux types;
 */
export type ExtendedNodeModel = TextNodeExtended | ImageNode | ShapeNode;
/**
 * Interface pour le document multi-pages;
 */
export interface DocumentModel {
  id: string;
  name: string;
  metadata: {
    width: number;
    height: number;
    unit: TemplateUnit;
    dpi: number;
  };
  pages: PageModel[];
}
export interface PageModel {
  id: string;
  name: string;
  backgroundColor: string;
  nodes: ExtendedNodeModel[];
}
/**
 * Types pour les interfaces BaseNode manquantes;
 */
export interface BaseNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  locked: boolean;
  visible: boolean;
}
export interface ShapeNode extends BaseNode {
  type: 'shape';
  shape: 'rect' | 'ellipse' | 'line' | 'arrow' | 'polygon';
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  cornerRadius?: number;
}
export interface ImageNode extends BaseNode {
  type: 'image';
  src: string;
  fit: 'fill' | 'contain' | 'cover';
  filters?: PhotoFilters;
}
/**
 * Types pour l'export de documents;
 */
export type ExportFormat = 'png' | 'jpg' | 'webp' | 'pdf' | 'svg' | 'json' | 'photobooth-json';
